'use client';

import { GlassView, isGlassEffectAPIAvailable, isLiquidGlassAvailable } from 'expo-glass-effect';
import { cssInterop } from 'nativewind';
import React, { useSyncExternalStore } from 'react';
import { AccessibilityInfo, Platform, type ViewProps } from 'react-native';
import { twMerge } from 'tailwind-merge';

import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { View } from '@/components/ui/view';
import { useColors } from '@/hooks/useColors';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

cssInterop(GlassView, { className: 'style' });

/** Hex alpha for a dimmed tint, about 40%, to match the fallback's disabled opacity. */
const DIMMED_TINT_ALPHA = '66';

/** Hex alpha for the dark tone's black tint, about 35%: enough to hold white content over a pale picture. */
const DARK_TINT_ALPHA = '59';

/** The dark tint when disabled, about 16%, so a disabled control fades like the other tones. */
const DARK_DIMMED_TINT_ALPHA = '29';

let isSupported: boolean | undefined;

/**
 * Whether this device can draw Liquid Glass at all: iOS 26 or later, built
 * with the iOS 26 SDK. `isGlassEffectAPIAvailable` is checked first because
 * some iOS 26 betas report glass as available and then crash drawing it.
 */
function isLiquidGlassSupported(): boolean {
  if (isSupported === undefined) {
    try {
      isSupported =
        Platform.OS === 'ios' && isGlassEffectAPIAvailable() && isLiquidGlassAvailable();
    } catch {
      isSupported = false;
    }
  }

  return isSupported;
}

/**
 * The reader's Reduce Transparency setting, shared by every glass component so
 * it is read once rather than on each mount. `undefined` until the first read
 * returns.
 */
let reduceTransparency: boolean | undefined;

const reduceTransparencyListeners = new Set<() => void>();

let isWatchingReduceTransparency = false;

function setReduceTransparency(enabled: boolean) {
  if (reduceTransparency === enabled) {
    return;
  }

  reduceTransparency = enabled;

  reduceTransparencyListeners.forEach((listener) => listener());
}

/**
 * Reads the setting and follows its changes, once for the app. The
 * subscription is never removed: it is one listener for the app's lifetime,
 * and keeping it is what keeps the cached value true between mounts.
 */
function watchReduceTransparency() {
  if (isWatchingReduceTransparency) {
    return;
  }

  isWatchingReduceTransparency = true;

  AccessibilityInfo.isReduceTransparencyEnabled()
    .then(setReduceTransparency)
    .catch(() => setReduceTransparency(false));

  AccessibilityInfo.addEventListener('reduceTransparencyChanged', setReduceTransparency);
}

function subscribeToReduceTransparency(listener: () => void) {
  if (isLiquidGlassSupported()) {
    watchReduceTransparency();
  }

  reduceTransparencyListeners.add(listener);

  return () => {
    reduceTransparencyListeners.delete(listener);
  };
}

function getReduceTransparency() {
  return reduceTransparency;
}

/*
 * Start the read at launch, so the answer is usually in by the time a glass
 * screen first draws.
 */
if (isLiquidGlassSupported()) {
  watchReduceTransparency();
}

/**
 * True when glass should be drawn: the device supports it and the reader has
 * not turned on Reduce Transparency, which asks for opaque surfaces instead.
 * Everywhere else, including Android and web, every glass component here draws
 * its opaque fallback.
 *
 * Until the setting has been read, this is false. Drawing glass on a guess
 * would show it, and float the composer, to a reader who asked for neither,
 * then re-lay the screen out once the answer arrived. Opaque first costs at
 * most a single change, on the first glass screen after launch.
 */
export function useLiquidGlass(): boolean {
  const isDeviceSupported = isLiquidGlassSupported();

  const isReduceTransparencyEnabled = useSyncExternalStore(
    subscribeToReduceTransparency,
    getReduceTransparency,
    getReduceTransparency
  );

  return isDeviceSupported && isReduceTransparencyEnabled === false;
}

type GlassTone = 'neutral' | 'primary' | 'dark';

type GlassSurfaceProps = ViewProps & {
  /** Shape and layout, applied on every platform. */
  className?: string;
  /** Fill and border for the opaque fallback, where glass is not drawn. */
  fallbackClassName?: string;
  /**
   * `primary` tints the glass with the brand color, for a surface that is the main action.
   * `dark` draws dark glass whatever the app's theme, for controls floating over a picture,
   * where the theme says nothing about what is behind them. Its content must be white.
   */
  tone?: GlassTone;
  /** Lets the glass react to touch, as iOS glass controls do. */
  isInteractive?: boolean;
  /** Fades a `primary` tint, for a disabled main action that should still read as the brand color. */
  isDimmed?: boolean;
};

/**
 * A Liquid Glass surface on iOS 26, and a plain view carrying
 * `fallbackClassName` everywhere else. Put shape (`rounded-*`) and layout in
 * `className` so both renderings share it; put fill and border only in
 * `fallbackClassName`, since glass draws its own.
 */
export const GlassSurface = React.forwardRef<React.ElementRef<typeof View>, GlassSurfaceProps>(
  (
    {
      className,
      fallbackClassName,
      tone = 'neutral',
      isInteractive = false,
      isDimmed = false,
      ...props
    },
    ref
  ) => {
    const hasGlass = useLiquidGlass();

    const { colors, theme } = useColors();

    if (!hasGlass) {
      return <View ref={ref} className={twMerge(className, fallbackClassName)} {...props} />;
    }

    const isDarkGlass = tone === 'dark' || theme.dark;

    let tintColor: string | undefined;

    if (tone === 'primary') {
      tintColor = `${colors.primary[500]}${isDimmed ? DIMMED_TINT_ALPHA : ''}`;
    } else if (tone === 'dark') {
      tintColor = `${colors.black}${isDimmed ? DARK_DIMMED_TINT_ALPHA : DARK_TINT_ALPHA}`;
    }

    return (
      <GlassView
        ref={ref}
        className={className}
        glassEffectStyle="regular"
        isInteractive={isInteractive}
        tintColor={tintColor}
        colorScheme={isDarkGlass ? 'dark' : 'light'}
        {...props}
      />
    );
  }
);

GlassSurface.displayName = 'GlassSurface';

/**
 * The one size of a glass icon button. It never shrinks (`shrink-0` on the
 * button) so a long title beside it in a row cannot squeeze it narrower than
 * its siblings. A spacer that stands in for a button takes this class too.
 */
export const GLASS_ICON_BUTTON_SIZE_CLASS = 'h-10 w-10';

type GlassIconButtonProps = {
  icon: React.ComponentProps<typeof Icon>['as'];
  accessibilityLabel: string;
  accessibilityHint?: string;
  onPress: () => void;
  disabled?: boolean;
  tone?: GlassTone;
  /** Icon size and color. Defaults suit a neutral header control. */
  iconClassName?: string;
  /** Fill for the opaque fallback. Defaults to the transparent header control with a web hover. */
  fallbackClassName?: string;
  className?: string;
};

/**
 * A 40px round icon button: an interactive glass circle on iOS 26, like the
 * system's own bar buttons, and the app's usual icon button everywhere else.
 * The hit area is widened to the minimum touch target either way.
 */
export function GlassIconButton({
  icon,
  accessibilityLabel,
  accessibilityHint,
  onPress,
  disabled = false,
  tone = 'neutral',
  iconClassName = 'h-5 w-5 text-typography-700',
  fallbackClassName = 'web:hover:bg-background-100',
  className,
}: GlassIconButtonProps) {
  const hasGlass = useLiquidGlass();

  /*
   * The fallback fill sits on the pressable, not the surface, so the
   * pressable's disabled opacity dims it as it always has. No overflow
   * clipping on the surface: it would cut the widened hit area off at 40px.
   */
  return (
    <GlassSurface
      className={twMerge(GLASS_ICON_BUTTON_SIZE_CLASS, 'shrink-0 rounded-full', className)}
      tone={tone}
      isDimmed={disabled}
      isInteractive={!disabled}>
      <Pressable
        onPress={onPress}
        disabled={disabled}
        hitSlop={ICON_BUTTON_HIT_SLOP}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        accessibilityState={{ disabled }}
        className={twMerge(
          'h-full w-full items-center justify-center rounded-full web:cursor-pointer',
          !hasGlass && fallbackClassName
        )}>
        <Icon as={icon} className={iconClassName} />
      </Pressable>
    </GlassSurface>
  );
}
